import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import api from '@src/api/clients';
import {
  approveBudgetCenter,
  discardBudgetCenterDraft,
  fetchBudgetCenter,
  saveBudgetCenterDraft,
} from '@src/api/actions/dashboard/clientBudgets';
import Page from '@src/components/layout/Page';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

function formatCurrency(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '$0.00';
  }
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) {
    return fallback;
  }
  if (typeof data === 'string') {
    return data;
  }
  if (data.detail) {
    return data.detail;
  }
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(', ');
}

function getCurrentMonthStart() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
}

function formatMonthLabel(value) {
  if (!value) {
    return 'Current Month';
  }
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function SummaryCard({ label, value, helper }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="overline">
            {label}
          </Typography>
          <Typography variant="h5">{value}</Typography>
          <Typography color="text.secondary" variant="body2">
            {helper}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function emptySegment(period) {
  return {
    budget_id: null,
    source_budget_id: null,
    vertical: '',
    location: '',
    channel: '',
    amount: '',
    notes: '',
    adjustment_total: '0.00',
    effective_amount: '',
    workflow_status: 'draft',
    adjustments: [],
    vertical_data: null,
    location_data: null,
    channel_data: null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: period,
      notes: '',
    },
  };
}

function normalizeSegment(segment, period) {
  return {
    budget_id: segment?.budget_id ?? segment?.id ?? null,
    source_budget_id: segment?.source_budget_id ?? segment?.id ?? null,
    vertical: segment?.vertical ?? segment?.vertical_data?.id ?? '',
    location: segment?.location ?? segment?.location_data?.id ?? '',
    channel: segment?.channel ?? segment?.channel_data?.id ?? '',
    amount: segment?.amount ?? '',
    notes: segment?.notes ?? '',
    adjustment_total: segment?.adjustment_total ?? '0.00',
    effective_amount: segment?.effective_amount ?? segment?.amount ?? '',
    workflow_status: segment?.workflow_status ?? 'draft',
    adjustments: Array.isArray(segment?.adjustments) ? segment.adjustments : [],
    vertical_data: segment?.vertical_data ?? null,
    location_data: segment?.location_data ?? null,
    channel_data: segment?.channel_data ?? null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: period,
      notes: '',
    },
  };
}

function segmentSignature(segment) {
  return [
    segment.vertical || '',
    segment.location || '',
    segment.channel || '',
    String(segment.amount || ''),
    String(segment.notes || ''),
  ].join('|');
}

function buildSegmentLabel(segment, options) {
  const verticalLabel = options.verticals.find((row) => String(row.value) === String(segment.vertical || ''))?.label;
  const locationLabel = options.locations.find((row) => String(row.value) === String(segment.location || ''))?.label;
  const channelLabel = options.channels.find((row) => String(row.value) === String(segment.channel || ''))?.label;
  return [verticalLabel, locationLabel, channelLabel].filter(Boolean).join(' / ') || 'Segment';
}

export default function BudgetCenterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const user = state?.user;
  const memberships = user?.client_memberships || [];
  const [selectedClient, setSelectedClient] = useState(searchParams.get('client_id') || '');
  const [selectedClientOption, setSelectedClientOption] = useState(null);
  const [period, setPeriod] = useState(searchParams.get('period') || getCurrentMonthStart());
  const [pageData, setPageData] = useState(null);
  const [segments, setSegments] = useState([emptySegment(period)]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [adjustmentsDialogOpen, setAdjustmentsDialogOpen] = useState(false);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const {
    options: clientOptions,
    loading: clientLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete();

  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const canShowClientSelector = isStaff || clientOptions.length > 1;

  useEffect(() => {
    api.request({ url: 'dashboard/verticals/', method: 'GET' })
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
        setVerticalOptions(rows.map((row) => ({ value: row.id, label: row.vertical })));
      })
      .catch(() => {});

    api.request({ url: 'dashboard/channels/', method: 'GET' })
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
        setChannelOptions(rows.map((row) => ({ value: row.id, label: row.name })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedClient) {
      const option = findOptionByValue(selectedClient);
      if (option) {
        setSelectedClientOption(option);
      }
      api.request({
        url: 'dashboard/client-territories/',
        method: 'GET',
        payload: { client_id: selectedClient },
      })
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
          setLocationOptions(rows.map((row) => ({ value: row.id, label: row.name })));
        })
        .catch(() => setLocationOptions([]));
      return;
    }

    const fallbackClientId = searchParams.get('client_id')
      || memberships.find((membership) => membership.client_id)?.client_id
      || (clientOptions.length === 1 ? clientOptions[0].value : '');
    if (fallbackClientId) {
      setSelectedClient(String(fallbackClientId));
    }
  }, [clientOptions, findOptionByValue, memberships, searchParams, selectedClient]);

  const loadBudgetCenter = useCallback(() => {
    if (!selectedClient) {
      return;
    }
    setLoading(true);
    fetchBudgetCenter({ client_id: selectedClient, period })
      .then((response) => {
        const payload = response.data;
        setPageData(payload);
        setSegments(
          Array.isArray(payload?.segments) && payload.segments.length
            ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
            : [emptySegment(payload?.period || period)]
        );
      })
      .catch((error) => {
        setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to load Budget Center.') });
      })
      .finally(() => setLoading(false));
  }, [period, selectedClient]);

  useEffect(() => {
    loadBudgetCenter();
  }, [loadBudgetCenter]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (selectedClient) {
      nextParams.set('client_id', String(selectedClient));
    } else {
      nextParams.delete('client_id');
    }
    if (period) {
      nextParams.set('period', period);
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [period, searchParams, selectedClient, setSearchParams]);

  const currentSummary = pageData?.current_budget || null;
  const previousSummary = pageData?.previous_budget || null;
  const optionSet = useMemo(
    () => ({ verticals: verticalOptions, locations: locationOptions, channels: channelOptions }),
    [verticalOptions, locationOptions, channelOptions]
  );
  const selectedSegment = selectedSegmentIndex === null ? null : segments[selectedSegmentIndex] || null;

  const buildPayload = useCallback(() => ({
    client_id: Number(selectedClient),
    period,
    allocation_mode: 'optimize_freely',
    allocation_config: {},
    notes: '',
    segments: segments.map((segment) => ({
      budget_id: segment.budget_id || null,
      source_budget_id: segment.source_budget_id || null,
      vertical: segment.vertical || null,
      vertical_ids: segment.vertical ? [Number(segment.vertical)] : [],
      location: segment.location || null,
      location_ids: segment.location ? [Number(segment.location)] : [],
      channel: segment.channel || null,
      amount: segment.amount,
      notes: segment.notes || '',
    })),
  }), [period, segments, selectedClient]);

  const runBudgetAction = useCallback(async (requestFn, successMessage) => {
    if (!selectedClient) {
      setBanner({ severity: 'error', message: 'Select a client first.' });
      return null;
    }
    setSaving(true);
    try {
      const response = await requestFn(buildPayload());
      const payload = response.data;
      setPageData(payload);
      setSegments(
        Array.isArray(payload?.segments) && payload.segments.length
          ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
          : [emptySegment(period)]
      );
      setBanner({ severity: 'success', message: successMessage });
      return payload;
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Budget action failed.') });
      return null;
    } finally {
      setSaving(false);
    }
  }, [buildPayload, period, selectedClient]);

  const saveDraft = useCallback(() => runBudgetAction(saveBudgetCenterDraft, 'Draft saved.'), [runBudgetAction]);
  const approveDraft = useCallback(() => runBudgetAction(approveBudgetCenter, 'Budget set approved.'), [runBudgetAction]);

  const discardDraft = useCallback(async () => {
    if (!selectedClient) {
      return;
    }
    setSaving(true);
    try {
      const response = await discardBudgetCenterDraft({
        client_id: Number(selectedClient),
        period,
      });
      const payload = response.data;
      setPageData(payload);
      setSegments(
        Array.isArray(payload?.segments) && payload.segments.length
          ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
          : [emptySegment(period)]
      );
      setBanner({ severity: 'success', message: 'Draft discarded.' });
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to discard draft.') });
    } finally {
      setSaving(false);
    }
  }, [period, selectedClient]);

  const updateSegment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, rowIndex) => (
      rowIndex === index ? { ...segment, [field]: value } : segment
    )));
  };

  const updatePendingAdjustment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, rowIndex) => (
      rowIndex === index
        ? {
          ...segment,
          pendingAdjustment: {
            ...(segment.pendingAdjustment || {}),
            [field]: value,
          },
        }
        : segment
    )));
  };

  const ensureDraftSegment = useCallback(async (segment) => {
    if (pageData?.has_draft && segment.budget_id && pageData?.draft_budget_ids?.includes(segment.budget_id)) {
      return segment.budget_id;
    }
    const signature = segmentSignature(segment);
    const payload = await saveDraft();
    if (!payload) {
      return null;
    }
    const matchingSegment = (payload.segments || []).find(
      (row) => segmentSignature(normalizeSegment(row, payload.period || period)) === signature
    );
    return matchingSegment?.budget_id || null;
  }, [pageData, period, saveDraft]);

  const addAdjustment = useCallback(async () => {
    if (selectedSegmentIndex === null || !selectedSegment) {
      return;
    }
    const pendingAdjustment = selectedSegment.pendingAdjustment || {};
    if (!pendingAdjustment.amount_delta || !pendingAdjustment.effective_date) {
      setBanner({ severity: 'error', message: 'Adjustment amount and effective date are required.' });
      return;
    }

    const draftBudgetId = await ensureDraftSegment(selectedSegment);
    if (!draftBudgetId) {
      return;
    }

    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${draftBudgetId}/adjustments/`,
        method: 'POST',
        payload: pendingAdjustment,
      });
      setBanner({ severity: 'success', message: 'Adjustment added.' });
      loadBudgetCenter();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to add adjustment.') });
    } finally {
      setSaving(false);
    }
  }, [ensureDraftSegment, loadBudgetCenter, selectedSegment, selectedSegmentIndex]);

  const deleteAdjustment = useCallback(async (adjustmentId) => {
    if (!selectedSegment?.budget_id || !pageData?.draft_budget_ids?.includes(selectedSegment.budget_id)) {
      setBanner({ severity: 'info', message: 'Save a draft before removing adjustments.' });
      return;
    }
    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${selectedSegment.budget_id}/adjustments/${adjustmentId}/`,
        method: 'DELETE',
      });
      setBanner({ severity: 'success', message: 'Adjustment removed.' });
      loadBudgetCenter();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to remove adjustment.') });
    } finally {
      setSaving(false);
    }
  }, [loadBudgetCenter, pageData, selectedSegment]);

  return (
    <Page title="Budget Center" fullWidth>
      <Stack spacing={3}>
        <Box>
          <Typography variant="body1">
            Review and manage the full monthly budget table for the client. If the selected month has not been built yet, it starts from the prior month&apos;s approved baseline. Changes stay in draft until the month set is explicitly approved.
          </Typography>
        </Box>

        {banner ? <Alert severity={banner.severity}>{banner.message}</Alert> : null}

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              {canShowClientSelector ? (
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    loading={clientLoading}
                    onChange={(_, option) => {
                      setSelectedClient(option?.value ? String(option.value) : '');
                      setSelectedClientOption(option || null);
                    }}
                    onInputChange={handleClientSearch}
                    options={clientOptions}
                    value={selectedClientOption}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.value ?? option?.label}>
                        {option?.label || ''}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Client" placeholder="Select client" />}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} md={5}>
                  <TextField
                    disabled
                    fullWidth
                    label="Client"
                    value={pageData?.client?.name || memberships.find((membership) => membership.client_id)?.client_name || ''}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Month"
                  type="date"
                  value={period}
                  onChange={(event) => setPeriod(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  disabled
                  fullWidth
                  label="Status"
                  value={String(pageData?.status || 'draft').replaceAll('_', ' ')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && selectedClient ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <SummaryCard
                  label={
                    pageData?.seeded_from_previous
                      ? `${formatMonthLabel(period)} Starting Baseline`
                      : `${formatMonthLabel(period)} Baseline`
                  }
                  value={formatCurrency(currentSummary?.amount)}
                  helper={
                    pageData?.seeded_from_previous
                      ? `Copied from ${formatMonthLabel(pageData?.seed_source_period)}. Edit this before saving this month's draft.`
                      : `${currentSummary?.segment_count || segments.length} active segments in this month.`
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SummaryCard
                  label={`${formatMonthLabel(pageData?.previous_period)} Baseline`}
                  value={formatCurrency(previousSummary?.effective_amount || previousSummary?.amount)}
                  helper={`${previousSummary?.segment_count || 0} segments in the prior month.`}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SummaryCard
                  label="Adjustment Total"
                  value={formatCurrency(currentSummary?.adjustment_total)}
                  helper="Net in-month changes across the visible monthly set."
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SummaryCard
                  label="Effective Budget"
                  value={formatCurrency(currentSummary?.effective_amount || currentSummary?.amount)}
                  helper="Current month baseline plus approved in-month adjustments."
                />
              </Grid>
            </Grid>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography variant="h6">Monthly Budget Table</Typography>
                      <Typography color="text.secondary" variant="body2">
                        One row per budget segment. This mirrors the existing Client Budgets table, but edits are saved as a monthly draft set.
                      </Typography>
                    </Box>
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      onClick={() => setSegments((prev) => [...prev, emptySegment(period)])}
                    >
                      Add Segment
                    </Button>
                  </Stack>

                  <Alert severity="info">
                    Budget segments are planning targets. Actual delivery may vary based on performance and platform conditions, and exact segment delivery is not guaranteed.
                  </Alert>

                  {pageData?.seeded_from_previous ? (
                    <Alert severity="success">
                      Started from {formatMonthLabel(pageData?.seed_source_period)} baseline. Adjust segments as needed before saving.
                    </Alert>
                  ) : null}

                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Vertical</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Channel</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Adjustments</TableCell>
                          <TableCell>Effective</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {segments.map((segment, index) => (
                          <TableRow key={`${segment.budget_id || 'new'}-${index}`} hover>
                            <TableCell sx={{ minWidth: 180 }}>
                              <TextField
                                fullWidth
                                select
                                size="small"
                                value={segment.vertical}
                                onChange={(event) => updateSegment(index, 'vertical', event.target.value)}
                              >
                                <MenuItem value="">None</MenuItem>
                                {verticalOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              <TextField
                                fullWidth
                                select
                                size="small"
                                value={segment.location}
                                onChange={(event) => updateSegment(index, 'location', event.target.value)}
                              >
                                <MenuItem value="">None</MenuItem>
                                {locationOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              <TextField
                                fullWidth
                                select
                                size="small"
                                value={segment.channel}
                                onChange={(event) => updateSegment(index, 'channel', event.target.value)}
                              >
                                <MenuItem value="">None</MenuItem>
                                {channelOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell sx={{ minWidth: 140 }}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                value={segment.amount}
                                onChange={(event) => updateSegment(index, 'amount', event.target.value)}
                              />
                            </TableCell>
                            <TableCell>{formatCurrency(segment.adjustment_total)}</TableCell>
                            <TableCell>{formatCurrency(segment.effective_amount || segment.amount)}</TableCell>
                            <TableCell>
                              <Chip
                                label={String(segment.workflow_status || 'draft').replaceAll('_', ' ')}
                                size="small"
                                color={pageData?.draft_budget_ids?.includes(segment.budget_id) ? 'warning' : 'default'}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              <TextField
                                fullWidth
                                size="small"
                                value={segment.notes}
                                onChange={(event) => updateSegment(index, 'notes', event.target.value)}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Button
                                size="small"
                                startIcon={<EditOutlinedIcon />}
                                onClick={() => {
                                  setSelectedSegmentIndex(index);
                                  setAdjustmentsDialogOpen(true);
                                }}
                              >
                                Adjustments
                              </Button>
                              <IconButton
                                onClick={() => setSegments((prev) => prev.filter((_, rowIndex) => rowIndex !== index))}
                                disabled={segments.length === 1}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                  <Stack spacing={0.5}>
                    <Typography variant="h6">Monthly Actions</Typography>
                    <Typography color="text.secondary" variant="body2">
                      Save a draft, review the table, then approve the full monthly set when ready.
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      disabled={saving}
                      startIcon={<SaveOutlinedIcon />}
                      variant="contained"
                      onClick={saveDraft}
                    >
                      Save Draft
                    </Button>
                    <Button
                      disabled={saving}
                      startIcon={<CheckCircleOutlineIcon />}
                      variant="outlined"
                      onClick={approveDraft}
                    >
                      Approve / Submit
                    </Button>
                    <Button
                      disabled={saving || !pageData?.has_draft}
                      startIcon={<UndoOutlinedIcon />}
                      variant="text"
                      onClick={discardDraft}
                    >
                      Discard Changes
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Dialog fullWidth maxWidth="md" onClose={() => setAdjustmentsDialogOpen(false)} open={adjustmentsDialogOpen}>
              <DialogTitle>Segment Adjustments</DialogTitle>
              <DialogContent>
                {selectedSegment ? (
                  <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="info">
                      Baseline budget stays separate from in-month changes. Use adjustments for increases or cuts that should affect only this segment.
                    </Alert>
                    <Typography variant="subtitle2">
                      {buildSegmentLabel(selectedSegment, optionSet)}
                    </Typography>
                    {(selectedSegment.adjustments || []).length ? (
                      (selectedSegment.adjustments || []).map((adjustment) => (
                        <Box key={adjustment.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                            <Box>
                              <Typography variant="body1">{formatCurrency(adjustment.amount_delta)}</Typography>
                              <Typography color="text.secondary" variant="body2">
                                Effective {dayjs(adjustment.effective_date).format('MMM D, YYYY')}
                              </Typography>
                              {adjustment.notes ? (
                                <Typography color="text.secondary" variant="body2">
                                  {adjustment.notes}
                                </Typography>
                              ) : null}
                            </Box>
                            <Button variant="text" onClick={() => deleteAdjustment(adjustment.id)}>
                              Remove
                            </Button>
                          </Stack>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No adjustments on this segment yet.
                      </Typography>
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={selectedSegment.pendingAdjustment?.amount_delta || ''}
                          onChange={(event) => updatePendingAdjustment(selectedSegmentIndex, 'amount_delta', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Effective Date"
                          type="date"
                          value={selectedSegment.pendingAdjustment?.effective_date || period}
                          onChange={(event) => updatePendingAdjustment(selectedSegmentIndex, 'effective_date', event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Notes"
                          value={selectedSegment.pendingAdjustment?.notes || ''}
                          onChange={(event) => updatePendingAdjustment(selectedSegmentIndex, 'notes', event.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAdjustmentsDialogOpen(false)}>Close</Button>
                <Button onClick={addAdjustment} variant="contained">
                  Add Adjustment
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : null}
      </Stack>
    </Page>
  );
}
